import { Flex } from "antd";
import dayjs from "dayjs";
import {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useMemo,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import { Debt } from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";

import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";
import { InvoiceInfoModal } from "@/pages/WorkPlan/Tasks/TakeDebt/InvoiceInfoModal";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { InvoiceCard } from "../InvoiceCard";
import { StyledDebtDropdown, StyledDebtDropdownHeader } from "../Styled";

interface ByMarketProps {
    currentMarketInvoiceIds: string[];
    setCurrentMarketInvoiceIds: Dispatch<SetStateAction<string[]>>;
    loading: boolean;
}

export const ByMarket: FC<ByMarketProps> = ({
    currentMarketInvoiceIds,
    setCurrentMarketInvoiceIds,
    loading,
}) => {
    const { id } = useParams();

    const { debts: currentDebts } = useAppSelector((state) => state.work);
    const { offlineMode, debts } = useAppSelector((state) => state.offline);

    const [selectedInvoice, setSelectedInvoice] = useState<Debt | null>(null);

    const invoicesMap: Record<string, Debt> = offlineMode
        ? debts?.[id as string] ?? {}
        : currentDebts ?? {};

    const selectedInvoiceIds = currentMarketInvoiceIds;

    const invoiceOptions = useMemo(
        () =>
            Object.values(invoicesMap ?? {})
                .filter(
                    (invoice) =>
                        !selectedInvoiceIds.includes(invoice.consignmentId),
                )
                .map((invoice) => ({
                    value: invoice.consignmentId,
                    label: (
                        <Flex
                            vertical
                            gap="10px"
                            style={{ padding: "8px", width: "100%" }}
                        >
                            <SecondaryText
                                fontSize={18}
                                style={{ textWrap: "wrap" }}
                            >
                                {invoice.consignmentId}
                            </SecondaryText>
                            <Flex justify="space-between" gap="8px">
                                <PrimaryText
                                    fontSize={18}
                                    style={{ textWrap: "wrap" }}
                                >
                                    {dayjs(invoice.shipmentDate).format(
                                        "DD.MM.YYYY",
                                    )}
                                </PrimaryText>
                                <PrimaryText
                                    fontSize={18}
                                    $textColor="accent"
                                    style={{
                                        textWrap: "wrap",
                                    }}
                                >
                                    {formatUAHStandard(invoice.debt)}
                                </PrimaryText>
                            </Flex>
                        </Flex>
                    ),
                    ...invoice,
                })),
        [invoicesMap, selectedInvoiceIds],
    );

    const handleInvoiceSelect = (consignmentId: string) => {
        setCurrentMarketInvoiceIds((prev) =>
            prev.includes(consignmentId) ? prev : [...prev, consignmentId],
        );
    };

    const handleInvoiceRemove = (consignmentId: string) => {
        if (loading) return;

        setCurrentMarketInvoiceIds((prev) =>
            prev.filter((id) => id !== consignmentId),
        );
    };

    const handleInvoiceInfoClick = (invoice: Debt) => {
        setSelectedInvoice(invoice);
    };

    const handleModalClose = useCallback(() => {
        setSelectedInvoice(null);
    }, [setSelectedInvoice]);

    const handleCopy = useCallback((debt: Debt) => {
        const { createdAt: _createdAt, context: _context, ...rest } = debt;

        copyObjectToClipboard(rest);
    }, []);

    return (
        <>
            <Flex vertical gap={16}>
                <StyledDebtDropdownHeader
                    popupRender={(menu) => (
                        <StyledDebtDropdown>{menu}</StyledDebtDropdown>
                    )}
                    options={invoiceOptions}
                    mode="multiple"
                    value={[]}
                    onSelect={(value: string) => handleInvoiceSelect(value)}
                    placeholder="Накладна NUM"
                    disabled={loading}
                />

                <Flex vertical gap={8}>
                    {Array.isArray(selectedInvoiceIds) &&
                        selectedInvoiceIds.map((invoiceId) => {
                            const invoice = invoicesMap?.[invoiceId];
                            if (!invoice) return null;

                            const { shipmentDate, debt, consignmentId } =
                                invoice;

                            return (
                                <InvoiceCard
                                    key={invoiceId}
                                    consignmentId={consignmentId}
                                    shipmentDate={shipmentDate}
                                    debt={debt}
                                    onInfo={() =>
                                        handleInvoiceInfoClick(invoice)
                                    }
                                    onRemove={() =>
                                        handleInvoiceRemove(invoiceId)
                                    }
                                    onCopy={() => handleCopy(invoice)}
                                />
                            );
                        })}
                </Flex>
            </Flex>

            <InvoiceInfoModal
                onClose={handleModalClose}
                selectedInvoice={selectedInvoice}
                debt={selectedInvoice?.debt}
                shipmentDate={selectedInvoice?.shipmentDate}
                open={!!selectedInvoice}
            />
        </>
    );
};
